/**
 * @file notice.ts
 * @module app/utils/notice
 * @description Versioned notice data source for the utrade-admin one-time management notice.
 * Each entry has a title/message pair in English and Swahili, keyed by an incrementing `version`.
 * getLatestNotice() resolves the entry with the highest version, which Notice.tsx compares
 * against the user's last-acknowledged version (cookie) to decide whether to show it again.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 2.1.2
 */

export type NoticeLang = 'en' | 'sw';

export type Notice = {
  /** Bump this when publishing a new message; users who saw an older version will see it again. */
  version: number;
  title: Record<NoticeLang, string>;
  /** Rendered as HTML (paragraphs, lists, inline styles, etc.) — keep this developer-authored, never user/API input. */
  message: Record<NoticeLang, string>;
  /** Optional last moment (local time) the notice is shown; after this it is hidden for everyone. */
  showUntil?: string;
};

// RTDK-235: add a new entry with an incremented version to publish a new notice.
export const NOTICES: Notice[] = [
  {
    version: 1,
    title: {
      en: 'NOTICE',
      sw: 'TANGAZO',
    },
    message: {
      en: `<p>It has come to our attention that several employees have been sharing their official login credentials, passwords, and access codes with other staff members. This unauthorized practice has directly resulted in security compromises and <strong>significant financial losses</strong> for the company.</p>
<h6 class="fw-bolder mt-5 mb-2">Mandatory Compliance Policy</h6>
<ol class="ps-4 mb-4">
  <li><strong>Strict Confidentiality:</strong> Your login credentials (usernames, passwords, PINs, multi-factor authentication codes) are strictly personal and confidential.</li>
  <li><strong>Zero Sharing Tolerance:</strong> You must <strong>NOT</strong> share your credentials with anyone&mdash;whether they are internal colleagues, managers, or external parties&mdash;under any circumstances.</li>
  <li><strong>Personal Responsibility:</strong> You are individually responsible and accountable for any activity conducted through your assigned company account.</li>
</ol>
<div class="border border-danger border-dashed rounded p-4 mb-4">
  <div class="fw-bolder text-danger mb-1">IMPORTANT WARNING</div>
  <div>Sharing credentials is a direct violation of company security policy and contract terms. Any employee found sharing or using another individual's credentials will face <strong>immediate disciplinary action</strong>, up to and including <strong>termination of employment</strong>.</div>
</div>
<h6 class="fw-bolder mt-5 mb-2">What You Need To Do Immediately:</h6>
<ul class="ps-4 mb-4">
  <li>If you have ever shared your password with a colleague, <strong>change it immediately</strong>.</li>
  <li>If you require access to specific software or files to perform your duties, request proper authorization through your supervisor/IT department rather than using a coworker's login.</li>
  <li>Report any requests for password sharing to Management or IT immediately.</li>
</ul>
<p class="mb-0">We appreciate your prompt attention and cooperation in keeping our organization secure.</p>`,
      sw: `<p>Imefikia usikivu wetu kwamba baadhi ya wafanyakazi wamekuwa wakishiriki taarifa zao rasmi za kuingia (login credentials), nywila, na misimbo ya ufikiaji na wafanyakazi wengine. Kitendo hiki kisicho halali kimesababisha moja kwa moja uvunjaji wa usalama na <strong>hasara kubwa za kifedha</strong> kwa kampuni.</p>
<h6 class="fw-bolder mt-5 mb-2">Sera ya Lazima ya Uzingatiaji</h6>
<ol class="ps-4 mb-4">
  <li><strong>Usiri Mkali:</strong> Taarifa zako za kuingia (majina ya mtumiaji, nywila, PIN, misimbo ya uthibitishaji wa hatua nyingi) ni za binafsi na siri kabisa.</li>
  <li><strong>Hakuna Uvumilivu wa Kushiriki:</strong> Hupaswi <strong>KAMWE</strong> kushiriki taarifa zako na mtu yeyote&mdash;iwe ni wafanyakazi wenzako, wasimamizi, au watu wa nje&mdash;chini ya hali yoyote ile.</li>
  <li><strong>Wajibu Binafsi:</strong> Wewe binafsi unawajibika na unahusika na shughuli zozote zinazofanywa kupitia akaunti yako ya kampuni.</li>
</ol>
<div class="border border-danger border-dashed rounded p-4 mb-4">
  <div class="fw-bolder text-danger mb-1">ONYO MUHIMU</div>
  <div>Kushiriki taarifa za kuingia ni ukiukwaji wa moja kwa moja wa sera ya usalama ya kampuni na masharti ya mkataba. Mfanyakazi yeyote atakayebainika kushiriki au kutumia taarifa za mtu mwingine atakabiliwa na <strong>hatua za kinidhamu za haraka</strong>, ikiwa ni pamoja na <strong>kufukuzwa kazi</strong>.</div>
</div>
<h6 class="fw-bolder mt-5 mb-2">Unachopaswa Kufanya Sasa Hivi:</h6>
<ul class="ps-4 mb-4">
  <li>Kama umewahi kushiriki nywila yako na mfanyakazi mwenzako, <strong>ibadilishe mara moja</strong>.</li>
  <li>Ikiwa unahitaji ufikiaji wa programu au faili maalum ili kutekeleza majukumu yako, omba idhini rasmi kupitia msimamizi wako/idara ya IT badala ya kutumia taarifa za mfanyakazi mwenzako.</li>
  <li>Ripoti ombi lolote la kushiriki nywila kwa Usimamizi au IT mara moja.</li>
</ul>
<p class="mb-0">Tunashukuru kwa umakini wako wa haraka na ushirikiano wako katika kulinda usalama wa shirika letu.</p>`,
    },
  },
  {
    version: 2,
    title: {
      en: 'SCHEDULED MAINTENANCE',
      sw: 'MATENGENEZO YALIYOPANGWA',
    },
    message: {
      en: `<p>The platform will be <strong>down for scheduled maintenance tonight between 12:00 AM and 4:00 AM</strong>.</p>
<p>During this time you will not be able to log in or use the system. Please save your work and complete any pending tasks before 12:00 AM.</p>
<p class="mb-0">We apologise for the inconvenience and thank you for your patience.</p>`,
      sw: `<p>Mfumo <strong>hautapatikana kwa ajili ya matengenezo yaliyopangwa usiku wa leo kati ya saa 6:00 usiku na saa 10:00 alfajiri</strong> (12:00 AM &ndash; 4:00 AM).</p>
<p>Katika muda huu hutaweza kuingia wala kutumia mfumo. Tafadhali hifadhi kazi yako na ukamilishe kazi zozote zinazosubiri kabla ya saa 6:00 usiku.</p>
<p class="mb-0">Tunaomba radhi kwa usumbufu na tunashukuru kwa uvumilivu wako.</p>`,
    },
    showUntil: '2026-10-31T23:59:59',
  },
];

export const getLatestNotice = (): Notice | undefined => {
  const latest = NOTICES.reduce<Notice | undefined>((latest, notice) => {
    if (!latest || notice.version > latest.version) {
      return notice;
    }
    return latest;
  }, undefined);
  // An expired latest notice hides the modal entirely instead of falling back to an older one.
  if (latest?.showUntil && new Date() > new Date(latest.showUntil)) {
    return undefined;
  }
  return latest;
};
